/**
 * Optional "Built for Every Decoration Method" (or similar) badge strip for
 * solution pages. Takes `methods: { eyebrow?, heading?, intro?, items: [{name}] }`.
 * Icons are auto-picked per decoration method name.
 */
import { motion } from "framer-motion";
import {
  Printer, Shirt, Layers, Palette, Flame, Zap, Sparkles, Circle,
  Wand2, Sun,
} from "lucide-react";

const ICONS = {
  "screen printing":  Printer,
  "screen print":     Printer,
  "embroidery":       Shirt,
  "embroider":        Shirt,
  "dtf":              Layers,
  "direct to film":   Layers,
  "dtg":              Palette,
  "direct to garment": Palette,
  "heat transfer":    Flame,
  "laser engraving":  Zap,
  "laser":            Zap,
  "uv printing":      Sparkles,
  "uv":               Sparkles,
  "pad printing":     Wand2,
  "pad print":        Wand2,
  "sublimation":      Sun,
  "wide format":      Layers,
};

function iconFor(label) {
  return ICONS[label.trim().toLowerCase()] || Circle;
}

export default function DecorationMethods({ methods }) {
  if (!methods || !Array.isArray(methods.items) || methods.items.length === 0) return null;

  return (
    <section
      data-testid="solution-decoration-methods-section"
      className="py-12 lg:py-16 bg-gradient-to-b from-white to-slate-50"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {(methods.eyebrow || methods.heading || methods.intro) && (
          <div className="text-center max-w-2xl mx-auto mb-10">
            {methods.eyebrow && (
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600">
                {methods.eyebrow}
              </span>
            )}
            {methods.heading && (
              <h2
                data-testid="solution-decoration-methods-heading"
                className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight"
              >
                {methods.heading}
              </h2>
            )}
            {methods.intro && (
              <p className="mt-3 text-sm lg:text-base text-slate-500 leading-relaxed">
                {methods.intro}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {methods.items.map((m, i) => {
            const Icon = iconFor(m.name);
            const slug = m.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
            return (
              <motion.div
                key={`${slug}-${i}`}
                data-testid={`solution-decoration-method-${slug}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.04, duration: 0.35 }}
                className="flex items-center gap-2.5 bg-white border border-slate-200 rounded-xl px-3 py-2.5 shadow-sm hover:border-sky-200 hover:shadow-md transition-all"
              >
                <span className="w-9 h-9 rounded-lg bg-sky-50 ring-1 ring-sky-100 flex items-center justify-center shrink-0">
                  <Icon className="h-4.5 w-4.5 text-sky-600" strokeWidth={2} />
                </span>
                <span className="text-xs sm:text-sm font-semibold text-slate-800">
                  {m.name}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
